import { __assign, __awaiter, __generator, __values } from "tslib";
import { useCapabilities } from '@office-start/capabilities-service';
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { getErrorHandler } from '@office-start/error-handler';
import { logErrorEvent } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { logError } from '@office-start/telemetry-service';
import { getUserStorageItem, setUserStorageItem, } from '@office-start/user-storage-service';
import * as React from 'react';
import create from 'zustand';
import { useDefaultHubApps } from './useDefaultHubApps';
import { useDefaultPinSetOverride } from './useDefaultPinSetOverride';
var APPBAR_USER_STORAGE_KEY = 'AppBarUserData';
function getUserStorageOptions(componentContext) {
    var correlationId = componentContext.correlationId, getAccessToken = componentContext.getAccessToken, getIdentity = componentContext.getIdentity, _a = componentContext.inMockMode, inMockMode = _a === void 0 ? false : _a, _b = componentContext.jwtAuthEnabled, jwtAuthEnabled = _b === void 0 ? false : _b, _c = componentContext.officeHomeApiRoot, officeHomeApiRoot = _c === void 0 ? '' : _c, workload = componentContext.workload;
    return {
        correlationId: correlationId,
        getAccessToken: getAccessToken,
        getIdentity: getIdentity,
        inMockMode: inMockMode,
        jwtAuthEnabled: jwtAuthEnabled,
        officeHomeApiRoot: officeHomeApiRoot,
        workload: workload,
    };
}
function getAppBarUserData(userStorageOptions) {
    return getUserStorageItem(APPBAR_USER_STORAGE_KEY, userStorageOptions);
}
function setAppBarUserData(userData, userStorageOptions) {
    return setUserStorageItem(APPBAR_USER_STORAGE_KEY, userData, userStorageOptions);
}
var useAppBarUserDataStore = create(function (set) { return ({
    userData: undefined,
    setUserData: function (userData) {
        set({ userData: userData });
    },
}); });
export var useAppBarUserData = function () {
    var _a = useAppBarUserDataStore(), userData = _a.userData, setUserData = _a.setUserData;
    var capabilities = useCapabilities().data;
    var context = useOfficeComponentContext().data;
    var defaultApps = useDefaultHubApps();
    var defaultPinSetOverride = useDefaultPinSetOverride();
    React.useEffect(function () {
        var isSubscribed = true;
        var fetchData = function () { return __awaiter(void 0, void 0, void 0, function () {
            var userStorageOptions, userData_1, defaultPinSet, saveVersion, defaultPinnedApps, defaultApps_1, defaultApps_1_1, _a, id, pinnedIndex;
            var e_1, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (!((capabilities === null || capabilities === void 0 ? void 0 : capabilities.appBarPinningEnabled) && context && defaultApps)) return [3 /*break*/, 2];
                        userStorageOptions = getUserStorageOptions(context);
                        return [4 /*yield*/, getAppBarUserData(userStorageOptions)];
                    case 1:
                        userData_1 = _c.sent();
                        defaultPinSet = capabilities.appBarDefaultPinSet;
                        saveVersion = 1;
                        if (defaultPinSetOverride !== undefined) {
                            // If any override is used, disregard any existing user data
                            userData_1 = undefined;
                            // Use the default pin set from the override
                            defaultPinSet = defaultPinSetOverride;
                        }
                        // Check if default pin set contains a non-empty string
                        if (defaultPinSet) {
                            // Start using User Data v2 with "Clean App Bar" default apps
                            saveVersion = 2;
                            if ((userData_1 === null || userData_1 === void 0 ? void 0 : userData_1.version) === 1) {
                                // Reset the data if existing user data is on v1
                                userData_1 = undefined;
                            }
                        }
                        if (!userData_1) {
                            defaultPinnedApps = [];
                            try {
                                for (defaultApps_1 = __values(defaultApps), defaultApps_1_1 = defaultApps_1.next(); !defaultApps_1_1.done; defaultApps_1_1 = defaultApps_1.next()) {
                                    _a = defaultApps_1_1.value, id = _a.id, pinnedIndex = _a.pinnedIndex;
                                    // Pin all apps by default with a pinnedIndex.
                                    if (pinnedIndex !== undefined) {
                                        defaultPinnedApps.push({
                                            id: id,
                                            pinnedIndex: pinnedIndex,
                                            pinSource: 'DefaultPinned',
                                        });
                                    }
                                }
                            }
                            catch (e_1_1) { e_1 = { error: e_1_1 }; }
                            finally {
                                try {
                                    if (defaultApps_1_1 && !defaultApps_1_1.done && (_b = defaultApps_1.return)) _b.call(defaultApps_1);
                                }
                                finally { if (e_1) throw e_1.error; }
                            }
                            // No valid user data was found. Create a default one.
                            userData_1 = {
                                version: saveVersion,
                                lastModifiedTimeUtc: Date.now(),
                                pinnedApps: defaultPinnedApps,
                            };
                        }
                        if (isSubscribed) {
                            setUserData(userData_1);
                        }
                        _c.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        fetchData().catch(function (error) {
            var _a;
            getErrorHandler().handleError(error);
            var message = "Error reading app bar user data: ".concat(error);
            logErrorEvent(KnownInstrumentationEvents.AppBarUserDataReadError, (_a = {},
                _a["Error" /* InstrumentationParam.Error */] = message,
                _a));
            // TODO: Remove above telemetry once OTEL migration complete
            logError({
                eventName: KnownInstrumentationEvents.AppBarUserDataReadError,
                errorType: 'AppBar_UserData_Error',
                message: message,
            });
        });
        return function () {
            isSubscribed = false;
        };
    }, [
        capabilities,
        context,
        defaultApps,
        defaultPinSetOverride,
        setUserData,
    ]);
    var setNewUserData = React.useCallback(function (newUserData) { return __awaiter(void 0, void 0, void 0, function () {
        var userStorageOptions;
        return __generator(this, function (_a) {
            if (context) {
                userStorageOptions = getUserStorageOptions(context);
                newUserData = __assign(__assign({}, newUserData), { lastModifiedTimeUtc: Date.now() });
                setAppBarUserData(newUserData, userStorageOptions)
                    .then(function () {
                    setUserData(newUserData);
                })
                    .catch(function (error) {
                    var _a;
                    getErrorHandler().handleError(error);
                    var message = "Error writing app bar user data: ".concat(error);
                    logErrorEvent(KnownInstrumentationEvents.AppBarUserDataWriteError, (_a = {},
                        _a["Error" /* InstrumentationParam.Error */] = message,
                        _a));
                    // TODO: Remove above telemetry once OTEL migration complete
                    logError({
                        eventName: KnownInstrumentationEvents.AppBarUserDataWriteError,
                        errorType: 'AppBar_UserData_Error',
                        message: message,
                    });
                });
            }
            return [2 /*return*/];
        });
    }); }, [context, setUserData]);
    return React.useMemo(function () { return [userData, setNewUserData]; }, [userData, setNewUserData]);
};
//# sourceMappingURL=useAppBarUserData.js.map